import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, Store, Users, BarChart3, Settings, 
  Menu, X, Bell, LogOut, ChevronDown, FileText, Shield, Tag, TrendingUp,
  UserPlus, Truck, Activity, CreditCard, Wand2, Ticket, Mail, ClipboardCheck, Archive, Server,
  ChevronLeft, ChevronRight, Sparkles, Wine
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { CurrencySelector } from '../../components/CurrencySelector';
import { NotificationCenter } from '../../components/NotificationCenter';

const menuItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/admin/setup-wizard', icon: Wand2, label: 'Setup Wizard' },
  { path: '/admin/server-health', icon: Server, label: 'Server Health' },
  { path: '/admin/subscriptions', icon: CreditCard, label: 'Subscriptions' },
  { path: '/admin/restaurants', icon: Store, label: 'Restaurants' },
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/applications', icon: UserPlus, label: 'Applications' },
  { path: '/admin/driver-documents', icon: ClipboardCheck, label: 'Driver Documents' },
  { path: '/admin/document-archives', icon: Archive, label: 'Document Archives' },
  { path: '/admin/drinks-catalog', icon: Wine, label: 'Drinks Catalog' },
  { path: '/admin/tickets', icon: Ticket, label: 'Support Tickets' },
  { path: '/admin/notifications', icon: Mail, label: 'Email Templates' },
  { path: '/admin/broadcast', icon: Bell, label: 'Broadcast' },
  { path: '/admin/driver-performance', icon: Activity, label: 'Driver Performance' },
  { path: '/admin/salesperson-payouts', icon: Sparkles, label: 'Salesperson Payouts' },
  { path: '/admin/reports', icon: BarChart3, label: 'Financial Reports' },
  { path: '/admin/analytics', icon: TrendingUp, label: 'Analytics' },
  { path: '/admin/promo-codes', icon: Tag, label: 'Promo Codes' },
  { path: '/admin/delivery-settings', icon: Truck, label: 'Delivery Rates' },
  { path: '/admin/audit', icon: FileText, label: 'Audit Logs' },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Persist collapsed state in localStorage
    const saved = localStorage.getItem('admin_sidebar_collapsed');
    return saved === 'true';
  });

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('admin_sidebar_collapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-screen bg-slate-900 text-white z-40 flex flex-col transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0
          ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}`}
        data-testid="admin-sidebar"
      >
        {/* Header with logo */}
        <div className="p-4 border-b border-slate-800 flex-shrink-0">
          <Link to="/" className="flex items-center gap-2">
            <svg viewBox="0 0 100 100" className="w-10 h-10 flex-shrink-0">
              <rect width="100" height="100" rx="20" fill="#F97316"/>
              <g fill="white">
                <rect x="18" y="25" width="4" height="50" rx="2"/>
                <rect x="25" y="25" width="3" height="20" rx="1.5"/>
                <rect x="18" y="25" width="3" height="20" rx="1.5"/>
                <rect x="11" y="25" width="3" height="20" rx="1.5"/>
                <ellipse cx="78" cy="35" rx="12" ry="15"/>
                <rect x="76" y="45" width="4" height="30" rx="2"/>
                <rect x="30" y="50" width="44" height="8" rx="4"/>
              </g>
            </svg>
            {!sidebarCollapsed && (
              <div className="overflow-hidden">
                <span className="font-bold text-lg block whitespace-nowrap">Hambre</span>
                <span className="text-xs text-slate-400 whitespace-nowrap">Super Admin</span>
              </div>
            )}
          </Link>
        </div>

        {/* Collapse/Expand button - desktop only */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-slate-700 hover:bg-slate-600 rounded-full items-center justify-center text-white shadow-md transition-colors z-50"
          data-testid="sidebar-collapse-btn"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>

        {/* Navigation */}
        <nav className="p-2 space-y-1 flex-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mx-1 transition-colors duration-200
                  ${isActive 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}`}
                onClick={() => setSidebarOpen(false)}
                title={sidebarCollapsed ? item.label : ''}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && (
                  <span className="whitespace-nowrap overflow-hidden">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout button */}
        <div className="p-2 border-t border-slate-800 flex-shrink-0">
          <Button
            variant="ghost"
            className={`w-full text-slate-400 hover:text-white hover:bg-slate-800 ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start'}`}
            onClick={logout}
            title={sidebarCollapsed ? 'Logout' : ''}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="ml-3">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className={`transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Top header */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-500" />
                <h1 className="text-lg font-semibold text-slate-900">Admin Panel</h1>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Currency Selector */}
              <CurrencySelector />
              
              {/* Notification Center */}
              <NotificationCenter />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                      <span className="text-sm font-semibold text-orange-600">
                        {user?.name?.charAt(0) || 'A'}
                      </span>
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/admin/settings">Settings</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} className="text-red-600">
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
